import { useMemo } from 'react';

import { clsx } from 'clsx';
import sharedStyles from 'src/common/components/GridCard/_shared-styles.module.scss';

import {
  ProductCardContext,
  ProductCardContextType,
} from '~/src/common/components/GridCard/ProductCard/ProductCardContext';
import { ProductCardSource } from '~/src/common/components/GridCard/ProductCard/types';
import { useGetProductCartFreeArticleCoupons } from '~/src/common/hooks/cart/useGetCartFreeArticleCoupons';
import { useUpdateCartProductQuantityWithDelivery } from '~/src/common/hooks/cart/useUpdateCartProductQuantityWithDelivery';
import {
  AddToCartProductEventSource,
  ExtraCartEventProperties,
} from '~/src/common/services/Tracker';
import { Cart } from '~/src/common/typings/cart';
import { getCartQuantity } from '~/src/common/utils/cart/cart';
import { useGetCart } from '~/src/queries/api-ecom/generated/api-ecom';
import { CartRelatedProduct, Product, RelatedProduct } from '~/src/typings/products/types';

import gridStyles from '../GridCard.module.scss';

import AvailableProductCard from './AvailableProductCard/AvailableProductCard';
import UnavailableProductCard from './UnavailableProductCard/UnavailableProductCard';

interface Props {
  className?: string;
  product: Product | RelatedProduct | CartRelatedProduct;
  source: ProductCardSource;
  eventSource: AddToCartProductEventSource;
  categoryName?: string;
  subcategory?: { id: string; name: string };
  onRefresh?: () => Promise<unknown>;
  onProductCardClick?: () => void;
  analyticsProperties?: ExtraCartEventProperties;
}

const ProductCard = ({
  className,
  product,
  source,
  eventSource,
  categoryName,
  subcategory,
  onRefresh,
  onProductCardClick,
  analyticsProperties,
}: Props) => {
  const { data: cart } = useGetCart<Cart>();
  const freeQuantity = useGetProductCartFreeArticleCoupons(product.id);

  const { availableQuantity } = product;

  const { canUpdateCart, updateQuantity } = useUpdateCartProductQuantityWithDelivery({
    product,
    onRefresh,
    analytics: {
      categoryId: subcategory?.id,
      categoryName,
      eventSource,
      marketingPosition: analyticsProperties?.['product position in the list'],
      source,
      subcategoryName: subcategory?.name,
    },
  });

  const productCardContextValue = useMemo(
    () =>
      ({
        product,
        analyticsData: {
          categoryName,
          subcategoryName: subcategory?.name,
          source,
          eventSource,
          otherAnalyticsProperties: analyticsProperties,
        },
        onUpdateQuantity: updateQuantity,
      }) satisfies ProductCardContextType,
    [product, categoryName, subcategory, eventSource, source, updateQuantity, analyticsProperties],
  );

  return (
    <article
      className={clsx(gridStyles.container, className)}
      style={{
        aspectRatio: sharedStyles['CARD_RATIO-card'],
      }}
      onClick={onProductCardClick}>
      <ProductCardContext.Provider value={productCardContextValue}>
        {availableQuantity !== 0 ? (
          <AvailableProductCard
            product={product}
            categoryName={categoryName}
            subcategoryName={subcategory?.name}
            canUpdateCart={canUpdateCart}
            cartQuantity={getCartQuantity(cart, product)}
            onDisabledPickerClick={updateQuantity}
            eventSource={eventSource}
            freeQuantity={freeQuantity}
          />
        ) : (
          <UnavailableProductCard
            product={product}
            categoryName={categoryName}
            subcategoryName={subcategory?.name}
            eventSource={eventSource}
          />
        )}
      </ProductCardContext.Provider>
    </article>
  );
};

export default ProductCard;
